import { Alert, AlertDescription, AlertTitle, Button, Input } from '@invent/wl-ui-kit-next/ui';
import { Lock } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { NotPermitted } from '@/design';
import { ApiError, ProblemType, saveFile, type IEtagged } from '@/domains/api';
import { formatDateTime } from '@/domains/survey';
import type {
  IExportColumn,
  IExportCondition,
  IExportEligibility,
  IExportPopulation,
  IExportTemplate
} from '../../api/ranking-process-contracts';
import { rankingProcessApi } from '../../api/ranking-process-api';
import { LoadError, useAdminData } from '../../components/admin-page';
import { formatDay } from '../../components/days';
import { rankingPath, TEAM_PATHS } from '../../paths';
import { SNAPSHOT_ANSWERS, SNAPSHOT_READ, rankingRefusalReason } from '../../rankings';
import { refusalMessage } from '../../refusal';
import { Checklist, type IChoice } from '../lists/advanced-filter';
import { sentence } from '../person-hub/said';
import { CycleChoice, useCycleChoice } from './cycle-choice';
import {
  CodeInput,
  ColumnRows,
  SOURCE_WORDS,
  Select,
  sentColumns,
  useFieldOffers,
  type IOffer,
  type ValueKind
} from './export-columns';
import { useRanking } from './ranking';
import { WholeSaveFooter, wholeSaveRefusal, type WholeSaid } from './whole-save';

/**
 * A ranking's Export for scoring tab and the List setup tab's template (SR-231, RP §9): the
 * list's export template — the filter that narrows the eligible population and the columns the
 * file carries — and the file itself for this edition, written from one cycle. Reachable for a
 * list and year no ranking holds yet: the template is the list's, and the export makes the edition.
 *
 * **The file reads one cycle** (SR-415): its submitted answers and its figures at the pin's as-of
 * date — the cycle the ranking is linked to, drawn fixed, else the one chosen here among the cycles
 * pinning the survey, the newest with an as-of date first. A pin with no as-of date is offered,
 * said so, and its download held, with the way to set one (Setup → Cycles). survey-api's 409
 * `answer-metric-column` is said in words if it comes anyway.
 *
 * **The download is the export of record, and it takes the snapshot** (SR-451, the owner's
 * choice (A); `frozen_at` on the wire): survey-api's `POST`, which makes the edition where none is
 * kept and snapshots a draft holding none. A later export reads the metrics archive as it stood at
 * the snapshot — nothing recorded after it, whoever recorded it — until *Update snapshot…* moves
 * it. The record is read again once the file lands, so the draft or its snapshot is drawn at once.
 *
 * **The template is edited whole and saved whole** (`PUT` under `If-Match`), as survey-api replaces
 * it; nothing here judges an entry — a code no field or question has, a label taken, a value of the
 * wrong shape is survey-api's 400, said with each entry's position. A save's answer is adopted with
 * its `ETag`; a change somebody saved meanwhile is refused with Reload, which drops the draft.
 *
 * **A code is chosen through the lists' field picker** (Anton's ask before the client demo,
 * 13 Oct), as `export-columns.tsx` offers it; the columns' rows are that module's too. **A
 * condition's values are chosen too** (Дмитрий's ask on the review stand, 15 Oct): a select's or
 * an option question's options — one for `is`, a checklist (Lists' own) for `is one of` — and a
 * date for before and after; anything else is typed. A value's kind decides more (the stand-up of
 * 16 Oct): the operators offered are those survey-api takes for it — before and after a date's,
 * `is true` a yes / no's — a number is typed in a decimal box, a date in a date box, a yes / no
 * chosen. The columns, up to 200, scroll inside the card.
 *
 * **The export is `rankings.manage`'s**, a bulk copy of internal fields; its name is this side's
 * (`<survey>-<list>-<year>-input.xlsx`, RP §9.3's), since the header naming it is not exposed. A 422
 * `export-too-large` says survey-api's way out: narrow the filter or drop columns.
 */

const CONDITION_SOURCES = ['internal', 'answer'] as const;
/** The operators a value of each kind takes — survey-api's own rule (`RankingExportTemplates`):
 *  before and after a date's alone, `is true` a yes / no's alone. A code of no known kind is
 *  offered every one. */
const OPS_BY_KIND: Readonly<Record<ValueKind | 'other', readonly string[]>> = {
  number: ['eq', 'in'],
  date: ['eq', 'in', 'before', 'after'],
  bool: ['true', 'eq'],
  other: ['eq', 'in']
};

const YES_NO: IChoice[] = [
  { code: 'true', label: 'Yes' },
  { code: 'false', label: 'No' }
];

const OP_WORDS: Record<string, string> = {
  eq: 'is',
  in: 'is one of',
  before: 'is before',
  after: 'is after',
  true: 'is true'
};

/** What the export's 409 `invalid-transition` reasons say (SR-415). */
const EXPORT_REFUSAL_WORDS: ReadonlyMap<string, string> = new Map([
  [
    'answer-metric-column',
    'The template has a question that is a figure — assets or production — as an answer column. Replace it with that figure’s column on the List setup tab.'
  ]
]);

/** The Export for scoring tab: the file for this edition, its filter and columns the list's
 *  template, set up on the List setup tab. */
export function RankingExportTab() {
  const { key, permitted } = useRanking();
  return (
    <div className="grid max-w-6xl gap-6">
      <ExportPanel permitted={permitted} />
      <p className="text-sm text-muted-foreground">
        Which participants the file holds beyond the eligible ones, and which extra columns it
        carries, is this list’s template —{' '}
        <Link className="underline" to={rankingPath(key, 'setup')}>
          set up on the List setup tab
        </Link>
        .
      </p>
    </div>
  );
}

/** What a refused export says — a 409 reason in words, else survey-api's own. */
function exportRefusal(failure: unknown): string {
  const reason = rankingRefusalReason(failure);
  if (reason !== null) {
    return EXPORT_REFUSAL_WORDS.get(reason) ?? sentence(refusalMessage(failure, reason));
  }
  if (failure instanceof ApiError && failure.isProblem(ProblemType.exportTooLarge)) {
    return sentence(`${failure.message} ${failure.problem?.detail ?? ''}`.trim());
  }
  return sentence(refusalMessage(failure, 'The scoring input could not be written'));
}

function ExportPanel({ permitted }: { permitted: boolean }) {
  const { key, overview, reread, snapshotChanges, setSnapshotOpen } = useRanking();
  const reasonId = React.useId();
  const choice = useCycleChoice();
  const [busy, setBusy] = React.useState(false);
  const [refusal, setRefusal] = React.useState<Nullable<string>>(null);
  // RNK-12's final file asks for the DD interview done; an analyst may export without it.
  const [ddRequired, setDdRequired] = React.useState(true);
  // The population drawn only under the query it answers: a changed cycle or DD box waits.
  const populationAsked = JSON.stringify([choice.code, choice.cycle?.asOfDate ?? null, ddRequired]);
  const population = useAdminData(
    async (signal) =>
      choice.cycle?.asOfDate
        ? { asked: populationAsked, answer: await rankingProcessApi.exportPopulation(key, choice.code, ddRequired, signal) }
        : null,
    [key.surveyCode, key.list, key.year, populationAsked]
  );
  const populationShown = population.data?.asked === populationAsked ? population.data.answer : null;

  const download = async () => {
    setBusy(true);
    setRefusal(null);
    try {
      const { file, fileName } = await rankingProcessApi.exportInput(key, choice.code, ddRequired);
      saveFile(file, fileName ?? `${key.surveyCode}-${key.list}-${key.year}-input.xlsx`);
      // The export may have made the draft or frozen it: the record says so at once.
      void reread();
    } catch (failure) {
      setRefusal(exportRefusal(failure));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      aria-label="Export"
      className="grid gap-3 rounded-md border border-border bg-white p-4"
    >
      <h2 className="text-base font-semibold text-[var(--color-royal-blue)]">Export</h2>
      <p className="text-sm text-muted-foreground">
        Every eligible participant (Travel is 1) who meets the template’s filter, whose figures
        came in as of the cycle’s as-of date and — unless you untick it below — whose DD interview
        is done (RNK-12’s final data file), with the template’s columns and empty Rank and Territory
        columns to score into — the file the Import scores tab takes back. Answers come from the cycle’s submitted responses; figures from the metrics archive, as
        of the cycle’s as-of date. Every row carries the cycle in a Cycle column and the snapshot in
        a Snapshot column: keep both in the scored file — the import links the draft to that cycle
        and checks the snapshot is still the ranking’s.
      </p>
      <SnapshotNote
        frozenAt={overview?.ranking.frozenAt ?? null}
        status={overview?.ranking.status ?? null}
        changes={snapshotChanges}
        onUpdate={() => setSnapshotOpen(true)}
      />
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={ddRequired} onChange={(event) => setDdRequired(event.target.checked)} />
        Only participants whose DD interview is done
      </label>
      {population.error && <LoadError message={population.error} />}
      {populationShown && <PopulationNote population={populationShown} survey={key.surveyCode} dd={ddRequired} />}
      <CycleChoice choice={choice} tab="export">
        <Button
          type="button"
          size="sm"
          disabled={!permitted || busy || choice.cycle?.asOfDate == null}
          aria-describedby={permitted ? undefined : reasonId}
          onClick={() => void download()}
        >
          {busy
            ? 'Writing the file…'
            : overview?.ranking.frozenAt || overview?.ranking.status === 'published'
              ? 'Download scoring input'
              : 'Download and take snapshot'}
        </Button>
      </CycleChoice>
      {!permitted && <NotPermitted id={reasonId} what="exporting a scoring input" />}
      {refusal && (
        <Alert variant="destructive">
          <AlertTitle>Not exported</AlertTitle>
          <AlertDescription>{refusal}</AlertDescription>
        </Alert>
      )}
    </section>
  );
}

/** Who the file holds, and who it leaves out and why — each shortfall a way to the stage that
 *  closes it: figures to Metrics updates, the interview to DD calls (the cycle's pipeline). */
function PopulationNote({ population, survey, dd }: { population: IExportPopulation; survey: string; dd: boolean }) {
  const surveyQuery = `?survey=${encodeURIComponent(survey)}`;
  return (
    <div className="grid gap-1 rounded-md bg-[var(--color-slight-blue)] p-3 text-sm" data-export-population>
      <p>
        <strong data-export-included={population.included}>{population.included.toLocaleString('en-US')}</strong> in the
        file, of {population.onList.toLocaleString('en-US')} eligible on this list (
        {population.eligible.toLocaleString('en-US')} eligible in all).
      </p>
      {(population.withoutFigures > 0 || (dd && population.withoutDd > 0)) && (
        <p className="text-muted-foreground">
          Left out:{' '}
          {population.withoutFigures > 0 && (
            <span data-export-without-figures={population.withoutFigures}>
              {population.withoutFigures.toLocaleString('en-US')} with no figures as of {formatDay(population.asOf)} —{' '}
              <Link className="underline" to={`${TEAM_PATHS.metricsUpdates}${surveyQuery}`}>
                Metrics updates
              </Link>
            </span>
          )}
          {population.withoutFigures > 0 && dd && population.withoutDd > 0 && '; '}
          {dd && population.withoutDd > 0 && (
            <span data-export-without-dd={population.withoutDd}>
              {population.withoutDd.toLocaleString('en-US')} without the DD interview —{' '}
              <Link className="underline" to={`${TEAM_PATHS.ddCalls}${surveyQuery}`}>
                DD calls
              </Link>
            </span>
          )}
          .
        </p>
      )}
    </div>
  );
}

interface IDraft {
  filter: IExportCondition[];
  columns: IExportColumn[];
}

export function TemplateEditor({
  surveyCode,
  list,
  permitted
}: {
  surveyCode: string;
  list: string;
  permitted: boolean;
}) {
  const { data, error, reload, adopt } = useAdminData<IEtagged<IExportTemplate>>(async (signal) => {
    try {
      return await rankingProcessApi.template(surveyCode, list, signal);
    } catch (readError) {
      throw new Error(refusalMessage(readError, 'Could not read the template'));
    }
  });
  const { internalOffer, offerOf } = useFieldOffers(surveyCode);
  const [draft, setDraft] = React.useState<Nullable<IDraft>>(null);
  const [drawn, setDrawn] = React.useState<Nullable<IExportTemplate>>(null);
  const [said, setSaid] = React.useState<Nullable<WholeSaid>>(null);
  const [busy, setBusy] = React.useState(false);
  // A template read or saved replaces the draft — the draft is always of the version shown.
  const template = data?.body ?? null;
  if (template !== drawn) {
    setDrawn(template);
    setDraft(template && { filter: template.filter, columns: template.columns });
  }

  if (template === null || draft === null) {
    return error ? (
      <LoadError message={error} />
    ) : (
      <p className="text-sm text-muted-foreground">Reading the template…</p>
    );
  }

  const dirty =
    JSON.stringify(draft) !==
    JSON.stringify({ filter: template.filter, columns: template.columns });
  const editable = permitted && !busy;
  const setCondition = (index: number, change: Partial<IExportCondition>) =>
    setDraft({
      ...draft,
      filter: draft.filter.map((entry, at) => (at === index ? { ...entry, ...change } : entry))
    });

  const save = async () => {
    if (data?.etag == null) {
      setSaid({
        kind: 'refused',
        text: 'This template came without its version — read it again.'
      });
      return;
    }
    setBusy(true);
    setSaid(null);
    try {
      const answer = await rankingProcessApi.saveTemplate(surveyCode, list, data.etag, {
        filter: draft.filter.map((condition) => ({
          ...condition,
          values: condition.values.filter(Boolean)
        })),
        columns: sentColumns(draft.columns)
      });
      adopt(answer);
      setSaid({
        kind: 'saved',
        text: `Saved — version ${answer.body.version}.`
      });
    } catch (failure) {
      setSaid(wholeSaveRefusal(failure, 'The template was refused'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      aria-label="Template"
      className="grid gap-4 rounded-md border border-border bg-white p-4"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold text-[var(--color-royal-blue)]">
          Template for this list
        </h2>
        <span className="text-sm text-muted-foreground" data-template-version={template.version}>
          {template.version === 0
            ? 'Not saved yet — no condition and no column'
            : `Version ${template.version}${template.updatedAt ? `, saved ${formatDateTime(template.updatedAt)}` : ''}`}
        </span>
      </div>

      <fieldset className="grid gap-2" disabled={!editable}>
        <legend className="mb-1 text-sm font-medium">Filter — every condition must hold</legend>
        {template.eligibility && (
          <FixedCondition eligibility={template.eligibility} offer={internalOffer} />
        )}
        {draft.filter.length === 0 && (
          <p className="text-sm text-muted-foreground">
            {template.eligibility
              ? 'No condition of the template’s own: every eligible participant.'
              : 'No condition: every eligible participant.'}
          </p>
        )}
        {draft.filter.map((condition, index) => {
          const n = index + 1;
          const offer = offerOf(condition.source);
          // A new field or source starts with no value, and an operator its kind does not take
          // gives way to the kind's first one.
          const reset = (source: string, code: string) => {
            const offered = OPS_BY_KIND[offerOf(source)?.kinds.get(code) ?? 'other'];
            return {
              op: offered.includes(condition.op) ? condition.op : offered[0],
              values: []
            };
          };
          const kind = offer?.kinds.get(condition.code) ?? null;
          return (
            <div key={index} data-condition={n} className="flex flex-wrap items-center gap-2">
              <Select
                label={`Condition ${n} source`}
                value={condition.source}
                options={CONDITION_SOURCES}
                words={SOURCE_WORDS}
                onChange={(source) =>
                  setCondition(index, { source, ...reset(source, condition.code) })
                }
              />
              <CodeInput
                label={`Condition ${n} code`}
                value={condition.code}
                offer={offer}
                onChange={(code) =>
                  setCondition(index, { code, ...reset(condition.source, code) })
                }
              />
              <Select
                label={`Condition ${n} operator`}
                value={condition.op}
                options={OPS_BY_KIND[kind ?? 'other']}
                words={OP_WORDS}
                onChange={(op) => setCondition(index, { op, values: valuesFor(op, condition) })}
              />
              <ValuesInput
                label={`Condition ${n} values`}
                condition={condition}
                kind={kind}
                choices={
                  offer?.choices.get(condition.code) ?? (kind === 'bool' ? YES_NO : null)
                }
                onChange={(values) => setCondition(index, { values })}
              />
              <Button
                type="button"
                size="sm"
                variant="ghost"
                aria-label={`Remove condition ${n}`}
                onClick={() =>
                  setDraft({
                    ...draft,
                    filter: draft.filter.filter((_, at) => at !== index)
                  })
                }
              >
                Remove
              </Button>
            </div>
          );
        })}
        <div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() =>
              setDraft({
                ...draft,
                filter: [...draft.filter, { source: 'internal', code: '', op: 'eq', values: [] }]
              })
            }
          >
            Add a condition
          </Button>
        </div>
      </fieldset>

      <fieldset className="grid gap-2" disabled={!editable}>
        <legend className="mb-1 text-sm font-medium">
          Columns — after the participant’s own, before Rank and Territory
        </legend>
        <ColumnRows
          columns={draft.columns}
          offerOf={offerOf}
          empty="No column of the template’s own."
          onChange={(columns) => setDraft({ ...draft, columns })}
        />
      </fieldset>

      <WholeSaveFooter
        what="this template"
        it="it"
        saveLabel="Save the template"
        notPermitted="changing a list’s scoring template"
        said={said}
        busy={busy}
        editable={editable}
        dirty={dirty}
        permitted={permitted}
        onSave={() => void save()}
        onDiscard={() => setDraft({ filter: template.filter, columns: template.columns })}
        onReload={() => void reload().then((read) => read && setSaid(null))}
      />
      {error && (
        <LoadError message={`Could not refresh — showing the template as last read. ${error}`} />
      )}
    </section>
  );
}

/**
 * A condition's values, shaped by its operator and what the code offers: none for `is true`, a
 * date for before and after, the field's or question's options — one chosen for `is`, a checklist
 * for `is one of` — and text otherwise (comma-separated for `is one of`). It shapes and judges
 * nothing: a stored value the options lack is kept and offered as spelled, and survey-api's 400
 * says a wrong one.
 */
function ValuesInput({
  label,
  condition,
  kind,
  choices,
  onChange
}: {
  label: string;
  condition: IExportCondition;
  kind: Nullable<ValueKind>;
  choices: Nullable<IChoice[]>;
  onChange: (values: string[]) => void;
}) {
  const { op, values } = condition;
  if (op === 'before' || op === 'after' || (op === 'eq' && kind === 'date')) {
    return (
      <Input
        type="date"
        aria-label={label}
        className="w-48"
        value={values[0] ?? ''}
        onChange={(event) => onChange(event.target.value === '' ? [] : [event.target.value])}
      />
    );
  }
  if (choices && op === 'in') {
    return (
      <Checklist
        label={label}
        choices={choices}
        values={values}
        invalid={false}
        onChange={(next) => onChange(next.map(String))}
      />
    );
  }
  if (choices && op === 'eq') {
    const held = values[0] ?? '';
    const shown =
      held === '' || choices.some((choice) => choice.code === held)
        ? choices
        : [...choices, { code: held, label: held }];
    return (
      <select
        aria-label={label}
        value={held}
        onChange={(event) => onChange(event.target.value === '' ? [] : [event.target.value])}
        className="h-9 w-64 rounded-md border border-border bg-white px-2 text-sm"
      >
        <option value="">Choose…</option>
        {shown.map((choice) => (
          <option key={choice.code} value={choice.code}>
            {choice.label}
          </option>
        ))}
      </select>
    );
  }
  const numeric = kind === 'number';
  return (
    <Input
      aria-label={label}
      className="w-64"
      inputMode={numeric ? 'decimal' : undefined}
      placeholder={
        op === 'in'
          ? numeric
            ? 'numbers, comma-separated'
            : kind === 'date'
              ? 'dates yyyy-MM-dd, comma-separated'
              : 'values, comma-separated'
          : numeric
            ? 'a number'
            : 'value'
      }
      disabled={op === 'true'}
      value={values.join(', ')}
      onChange={(event) => onChange(splitValues(event.target.value))}
    />
  );
}

/**
 * The fixed eligibility condition (Requirements v1.6, the owner's ruling of 19 Oct), drawn as the
 * filter's first row — the same controls as any condition, locked, with why: survey-api applies it
 * to every file and no template can drop it. The field and option are named by the staff form
 * where it was read, else by code.
 */
function FixedCondition({
  eligibility,
  offer
}: {
  eligibility: IExportEligibility;
  offer: Nullable<IOffer>;
}) {
  const field = (offer?.sections ?? [])
    .flatMap((section) => section.fields)
    .find((entry) => entry.code === eligibility.code);
  const choices = offer?.choices.get(eligibility.code) ?? [];
  const values = eligibility.values
    .map((code) => choices.find((choice) => choice.code === code)?.label ?? code)
    .join(', ');
  const box = 'flex h-9 items-center rounded-md border border-border bg-muted px-2.5 text-sm';
  return (
    <div
      data-condition-fixed=""
      aria-label="Fixed condition: eligible participants only"
      role="group"
      className="flex flex-wrap items-center gap-2"
    >
      <span className={box}>{SOURCE_WORDS.internal}</span>
      <span className={`${box} w-80 gap-1.5`}>
        {field?.label ?? eligibility.code}
        <span className="font-mono text-xs text-muted-foreground">{eligibility.code}</span>
      </span>
      <span className={box}>{eligibility.values.length > 1 ? 'is one of' : 'is'}</span>
      <span className={`${box} w-64`}>{values}</span>
      <span className="flex items-center gap-1 text-sm text-muted-foreground">
        <Lock className="size-3.5" aria-hidden />
        Fixed: eligible participants only (Requirements v1.6)
      </span>
    </div>
  );
}

/** What a condition's values become under another operator: only what the new one can draw — so
 *  nothing is sent that the screen does not show. A date never carries over to or from an option
 *  or a text, `is` keeps the first value, `is true` none. */
function valuesFor(op: string, condition: IExportCondition): string[] {
  const dated = (one: string) => one === 'before' || one === 'after';
  if (op === 'true' || dated(op) !== dated(condition.op)) return [];
  return op === 'eq' ? condition.values.slice(0, 1) : condition.values;
}

/** A values box's text as the condition's array: comma-separated, each trimmed, empties dropped. */
function splitValues(text: string): string[] {
  return text
    .split(',')
    .map((value) => value.trim())
    .filter((value, index, all) => value !== '' || index < all.length - 1);
}

/** Where the file's figures come from, said apart from the rest (SR-451): the snapshot of the
 *  metrics archive the ranking holds and what moved since — with the press that updates it — or,
 *  on a draft without one, that the download takes it. The answer columns are said not to be in
 *  it. */
function SnapshotNote({
  frozenAt,
  status,
  changes,
  onUpdate
}: {
  frozenAt: Nullable<string>;
  status: Nullable<string>;
  changes: Nullable<number>;
  onUpdate: () => void;
}) {
  // No edition at the address yet (a typed URL — Start makes one): the download makes it and
  // takes its snapshot, said as on a draft without one.
  const tone = 'grid gap-1 rounded-md border p-3 text-sm';
  if (frozenAt === null) {
    return status === 'published' ? (
      <p className="text-sm text-muted-foreground" data-freeze-note>
        This ranking holds no snapshot: the figures are read from the metrics archive as it stands
        now. {SNAPSHOT_ANSWERS}
      </p>
    ) : (
      <div className={`${tone} border-amber-300 bg-amber-50`} data-freeze-note>
        <strong>Downloading takes the snapshot.</strong>
        <span>
          The first download keeps this edition as a draft and takes its snapshot of the metrics
          archive now: every later export reads the figures as they stood then, until the snapshot
          is updated. {SNAPSHOT_ANSWERS}
        </span>
      </div>
    );
  }
  return (
    <div className={`${tone} border-border bg-muted/40`} data-freeze-note>
      <span>
        <strong>Snapshot {formatDateTime(frozenAt)}</strong>
        {changes !== null && (
          <span className="text-muted-foreground">
            {' '}
            · {changes === 0 ? 'no change since' : `${changes} ${changes === 1 ? 'change' : 'changes'} since`}
          </span>
        )}
        {status === 'draft' && (
          <>
            {' · '}
            <button type="button" className="text-primary underline" onClick={onUpdate}>
              Update snapshot…
            </button>
          </>
        )}
      </span>
      <span className="text-muted-foreground">
        The file reads the metrics archive as it stood then: {SNAPSHOT_READ}. {SNAPSHOT_ANSWERS}
      </span>
    </div>
  );
}
